// Cartão de uma conexão: status, saúde, última sincronização, permissões e ações.
import { useState } from 'react'
import { AlertTriangle, CheckCircle2, RefreshCw } from 'lucide-react'
import type { ConnectionDto } from '@impulso/shared/api/connections'
import { PlatformLogo } from '../../ui'
import { brand, statusBadge, timeAgo } from './format'
import '../account/pages.css'

interface Props {
  connection: ConnectionDto
  canManage: boolean
  canSync: boolean
  compact?: boolean
  syncing?: boolean
  onSync?: () => void
  onDisconnect?: () => void
  onReconnect?: () => void
}

export default function ConnectionCard({ connection: c, canManage, canSync, compact, syncing, onSync, onDisconnect, onReconnect }: Props) {
  const [confirm, setConfirm] = useState(false)
  const badge = statusBadge(c)
  const running = c.syncStates.some((s) => s.running)
  return (
    <div className="card" style={{ display: 'flex', gap: 14 }}>
      <PlatformLogo platform={brand(c.provider)} size="lg" />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div className="row between wrap" style={{ gap: 8 }}>
          <h3 style={{ overflowWrap: 'anywhere' }}>{c.providerName}</h3>
          <span className={`badge ${badge.cls}`}>
            {badge.cls === 'green' ? <CheckCircle2 size={12} /> : badge.cls === 'blue' ? <RefreshCw size={12} className="spin" /> : <AlertTriangle size={12} />} {badge.text}
          </span>
        </div>
        <div className="small muted" style={{ overflowWrap: 'anywhere' }}>
          {c.externalAccountName ?? c.externalAccountId} · {c.importing ? 'trazendo os últimos 13 meses de dados…' : `atualizado ${timeAgo(c.lastSyncAt)}`}
        </div>
        {c.healthIssues.length > 0 && (
          <ul className="small mt-8" style={{ color: c.healthLevel === 'bad' ? 'var(--red)' : 'var(--amber)', paddingLeft: 18, margin: '8px 0 0' }}>
            {c.healthIssues.map((i) => <li key={i}>{i}</li>)}
          </ul>
        )}
        {c.status === 'needs_reconnect' && (
          <div className="callout amber mt-8" style={{ flexDirection: 'column', gap: 4 }}>
            <span className="small strong">Como reconectar</span>
            <span className="small">1. Clique em "Conectar de novo". 2. Entre na mesma conta de {c.providerName} e aceite as permissões. Seu histórico e suas campanhas continuam aqui, e o Seu Impulso volta a atualizar sozinho.</span>
            {!canManage && <span className="small">Peça para um gestor da empresa fazer isso.</span>}
          </div>
        )}
        {!compact && c.importing && (
          <div className="small muted mt-8">Isso costuma levar alguns minutos. Você pode continuar usando o Seu Impulso enquanto isso.</div>
        )}
        {!compact && (
          <div className="row wrap mt-8" style={{ gap: 6 }}>
            {c.permissions.map((p) => <span key={p} className="badge" style={{ fontWeight: 500 }}>{p}</span>)}
          </div>
        )}
        {!compact && c.authorizedBy && (
          <div className="small muted mt-8">Autorizado por {c.authorizedBy}{c.authorizedVia === 'link' ? ' (pelo link de conexão)' : ''}.</div>
        )}
        <div className="row wrap mt-12" style={{ gap: 8 }}>
          {(c.status === 'needs_reconnect' || c.tokenExpiresSoon) && canManage && onReconnect && (
            <button className="btn primary sm" onClick={onReconnect}>Conectar de novo</button>
          )}
          {c.status === 'active' && canSync && onSync && (
            <button className="btn sm" onClick={onSync} disabled={syncing || running || c.importing}>
              <RefreshCw size={14} className={syncing || running ? 'spin' : undefined} /> {syncing || running ? 'Sincronizando…' : 'Sincronizar agora'}
            </button>
          )}
          {canManage && onDisconnect && !confirm && (
            <button className="btn ghost danger sm" onClick={() => setConfirm(true)}>Desconectar</button>
          )}
          {confirm && (
            <div className="callout amber" style={{ width: '100%', flexDirection: 'column', gap: 8 }} role="alertdialog" aria-label="Confirmar desconexão">
              <span className="small">Desconectar {c.externalAccountName ?? c.providerName}? Os anúncios continuam rodando na plataforma, mas o Seu Impulso para de acompanhar e ajustar essa conta.</span>
              <div className="row" style={{ gap: 8 }}>
                <button className="btn sm" onClick={() => setConfirm(false)}>Cancelar</button>
                <button className="btn primary danger sm" onClick={() => { setConfirm(false); onDisconnect?.() }}>Sim, desconectar</button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
